import React, { useEffect, useState } from 'react';
import { useOne, useInvalidate } from '@refinedev/core';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import { Card, Tabs, Typography, Tag, Button, Tooltip, Avatar } from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ManOutlined,
  WomanOutlined,
  PlusOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  ExportOutlined,
  EyeOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { usePatientFormFields, EditForm, CreateForm } from '@src/components/shared/forms';
import { useAppointmentsFormFields } from '../../appointments/hooks';
import {
  FaBirthdayCake,
  FaWhatsapp,
  FaUserMd,
  FaSpa,
  FaFire,
} from 'react-icons/fa';
import { useTypeRender } from '../hooks/useTypeRender';
import { ClinicInfoTab } from './tabs/clinic-info/ClinicInfoTab';
// import { DocumentsTab } from './tabs/documents/DocumentsTab';
import { PrescriptionsTab } from './tabs/prescriptions/PrescriptionsTab';
import { TransactionsTab } from './tabs/transactions/TransactionsTab';
import { TreatmentsTab } from './tabs/treatments/TreatmentsTab';
import { InvoicesTab } from './tabs/invoices/InvoicesTab';
import { ImagesTab } from './tabs/images/ImagesTab';

import dayjs from 'dayjs';

const { Title } = Typography;

const DEFAULT_TAB = 'clinic-info';

// Tab name mapping for better display
const getTabDisplayName = (tabKey: string): string => {
  switch (tabKey) {
    case 'clinic-info':
      return 'Clinic Info';
    case 'invoices':
      return 'Invoices';
    case 'prescription':
      return 'Prescriptions';
    case 'treatments':
      return 'Treatments';
    case 'transactions':
      return 'Transactions';
    case 'images':
      return 'Images';
    default:
      return 'Patient Details';
  }
};

export const PatientDetailsPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentTab = searchParams.get('tab') || DEFAULT_TAB;
  const [editFormVisible, setEditFormVisible] = useState(false);
  const [createAppointmentFormVisible, setCreateAppointmentFormVisible] = useState(false);
  const [autoEditTreatmentId, setAutoEditTreatmentId] = useState<string | null>(
    null
  );
  const invalidate = useInvalidate();

  // Get treatment initial values from URL params
  const treatmentInitialValuesParam = searchParams.get('treatmentInitialValues');
  const treatmentInitialValues = treatmentInitialValuesParam
    ? JSON.parse(decodeURIComponent(treatmentInitialValuesParam))
    : undefined;
  const autoOpenTreatmentCreate = searchParams.get('autoOpenTreatmentCreate') === 'true';

  // Clear treatment params after they're used (with longer delay to ensure form is fully loaded)
  useEffect(() => {
    if (autoOpenTreatmentCreate && treatmentInitialValues) {
      // Clear the params after a longer delay to ensure form is fully opened and values are set
      const timer = setTimeout(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('autoOpenTreatmentCreate');
        newSearchParams.delete('treatmentInitialValues');
        setSearchParams(newSearchParams, { replace: true });
      }, 3000); // Increased to 3 seconds to ensure form values are set
      return () => clearTimeout(timer);
    }
  }, [autoOpenTreatmentCreate, treatmentInitialValues, searchParams, setSearchParams]);

  // Check if mobile view
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const { data, isLoading } = useOne({
    resource: 'patients',
    id: id || '',
  });

  const patient = data?.data;

  const patientData = {
    ...patient,
    mobile_no: patient?.mobile_no?.substring(2),
  };

  const formFields = usePatientFormFields();
  const appointmentFormFields = useAppointmentsFormFields();
  const getTypeConfig = useTypeRender();

  // Helper function to get patient types for badges
  const getPatientTypeBadges = (patient: any) => {
    if (!patient?.type) return null;
    
    // Ensure type is a string and handle different formats
    const typeString = String(patient.type || '');
    const types = typeString.includes(',') 
      ? typeString.split(',').map((t: string) => t.trim()).filter(Boolean)
      : [typeString].filter(Boolean);
    
    return types.map((type: string, index: number) => {
      if (!type || typeof type !== 'string') return null;
      
      // Get color based on type
      const getTypeColor = (typeStr: string) => {
        const normalizedType = typeStr.toLowerCase().trim();
        switch (normalizedType) {
          case 'clinical':
            return { bg: '#e6f7ff', color: '#1890ff' };
          case 'aesthetic':
            return { bg: '#fff0f6', color: '#eb2f96' };
          case 'lhr':
            return { bg: '#fff7e6', color: '#fa8c16' };
          default:
            return { bg: '#f6ffed', color: '#52c41a' };
        }
      };
      
      const colors = getTypeColor(type);
      
      return (
        <Tag
          key={`type-${index}`}
          style={{
            backgroundColor: colors.bg,
            color: colors.color,
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            padding: '4px 8px',
          }}
        >
          {type}
        </Tag>
      );
    }).filter(Boolean); // Remove any null values
  };

  // Effect to handle auto-edit treatment
  useEffect(() => {
    const storedTreatmentId = localStorage.getItem('autoEditTreatmentId');
    const storedTimestamp = localStorage.getItem('autoEditTimestamp');

    if (storedTreatmentId && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp);
      const currentTime = Date.now();

      // Only process if the timestamp is recent (within 10 seconds)
      if (currentTime - timestamp < 10000) {
        // Clear it from localStorage to prevent it from persisting
        localStorage.removeItem('autoEditTreatmentId');
        localStorage.removeItem('autoEditTimestamp');

        // Set the auto edit treatment ID
        setAutoEditTreatmentId(storedTreatmentId);

        // Clear the auto edit ID after a longer delay to ensure the form opens properly
        const timer = setTimeout(() => {
          setAutoEditTreatmentId(null);
        }, 3000);

        return () => clearTimeout(timer);
      } else {
        // Clear old entries
        localStorage.removeItem('autoEditTreatmentId');
        localStorage.removeItem('autoEditTimestamp');
      }
    }
  }, []);

  // Update document title when tab changes
  useEffect(() => {
    if (patient) {
      const tabName = getTabDisplayName(currentTab);
      const patientName =
        `${patient.first_name} ${patient.last_name || ''}`.trim();
      document.title = `${tabName} - ${patientName}`;
    }
  }, [currentTab, patient]);

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const handleTabClick = (key: string) => {
    setSearchParams({ tab: key });
  };

  const handleTabIconClick = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (patient?.id) {
      const currentUrl = window.location.href;
      const baseUrl = currentUrl.split('?')[0];
      const newUrl = `${baseUrl}?tab=${key}`;
      window.open(newUrl, '_blank');
    }
  };

  // Function to handle back button click
  const handleBackClick = () => {
    navigate('/patients');
  };

  // Function to handle edit patient
  const handleEditPatient = () => {
    setEditFormVisible(true);
  };

  // Function to handle create appointment
  const handleCreateAppointment = () => {
    setCreateAppointmentFormVisible(true);
  };

  // Function to handle view appointments
  const handleViewAppointments = () => {
    if (patient?.first_name) {
      const patientName = `${patient.first_name} ${patient.last_name || ''}`.trim();
      const appointmentsUrl = `/appointments?search=${encodeURIComponent(patientName)}`;
      window.open(appointmentsUrl, '_blank');
    }
  };

  // Function to handle appointment creation success
  const handleAppointmentCreated = (data: any) => {
    setCreateAppointmentFormVisible(false);
    
    // Invalidate appointments cache to refresh the data
    invalidate({
      resource: 'appointments',
      invalidates: ['list', 'many'],
    });
  };

  // Function to handle patient edit success
  const handlePatientEdited = (data: any) => {
    setEditFormVisible(false);

    // Invalidate patient cache to refresh the data
    invalidate({
      resource: 'patients',
      invalidates: ['list', 'many'],
    });

    // Refresh the page to show updated data
    window.location.reload();
  };

  const items = [
    {
      key: 'clinic-info',
      label: (
        <span className="flex items-center">
          <MedicineBoxOutlined style={{ marginRight: '8px' }} />
          Clinic-Info
          <ExportOutlined
            style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }}
            onClick={e => handleTabIconClick('clinic-info', e)}
            className="hover:text-blue-600 cursor-pointer"
          />
        </span>
      ),
      children: (
        <div className="flex-1 flex flex-col overflow-auto p-0">
          <ClinicInfoTab patient={patient} />
        </div>
      ),
    },
    {
      key: 'invoices',
      label: (
        <span className="flex items-center">
          <FileTextOutlined style={{ marginRight: '8px' }} />
          Invoices
          <ExportOutlined
            style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }}
            onClick={e => handleTabIconClick('invoices', e)}
            className="hover:text-blue-600 cursor-pointer"
          />
        </span>
      ),
      children: (
        <div className="flex-1 flex flex-col overflow-auto p-0">
          <InvoicesTab patientId={Number(patient?.id) || 0} patient={patient} />
        </div>
      ),
    },
    {
      key: 'prescriptions',
      label: (
        <span className="flex items-center">
          <MedicineBoxOutlined style={{ marginRight: '8px' }} />
          Prescriptions
          <ExportOutlined
            style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }}
            onClick={e => handleTabIconClick('prescriptions', e)}
            className="hover:text-blue-600 cursor-pointer"
          />
        </span>
      ),
      children: (
        <div className="flex-1 flex flex-col overflow-auto p-0">
          <PrescriptionsTab patientId={patient?.id || 0} patient={patient} />
        </div>
      ),
    },
    {
      key: 'treatments',
      label: (
        <span className="flex items-center">
          <UserOutlined style={{ marginRight: '8px' }} />
          Treatments
          <ExportOutlined
            style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }}
            onClick={e => handleTabIconClick('treatments', e)}
            className="hover:text-blue-600 cursor-pointer"
          />
        </span>
      ),
      children: (
        <div className="flex-1 flex flex-col overflow-auto p-0">
          <TreatmentsTab
            patientId={patient?.id}
            autoEditTreatmentId={autoEditTreatmentId}
            treatmentInitialValues={treatmentInitialValues}
            autoOpenCreate={autoOpenTreatmentCreate}
          />
        </div>
      ),
    },
    {
      key: 'transactions',
      label: (
        <span className="flex items-center">
          <DollarOutlined style={{ marginRight: '8px' }} />
          Transactions
          <ExportOutlined
            style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }}
            onClick={e => handleTabIconClick('transactions', e)}
            className="hover:text-blue-600 cursor-pointer"
          />
        </span>
      ),
      children: (
        <div className="flex-1 flex flex-col overflow-auto p-0">
          <TransactionsTab patientId={patient?.id} />
        </div>
      ),
    },
    {
      key: 'images',
      label: (
        <span className="flex items-center">
          <FileTextOutlined style={{ marginRight: '8px' }} />
          Images
          <ExportOutlined
            style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }}
            onClick={e => handleTabIconClick('images', e)}
            className="hover:text-blue-600 cursor-pointer"
          />
        </span>
      ),
      children: (
        <div className="flex-1 flex flex-col overflow-auto p-0">
          <ImagesTab patientId={patient?.id} />
        </div>
      ),
    },
  ];

  if (isMobile) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                type="text"
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackClick}
                className="flex items-center justify-center w-8 h-8"
              />
              <Title level={4} className="!mb-0 !text-gray-800">
                Patient Details
              </Title>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={handleEditPatient}
                className="flex items-center justify-center w-8 h-8"
              />
            </div>
          </div>
        </div>

        {/* Patient Information Card - Fixed at Top */}
        <div className="flex-shrink-0 bg-gray-50">
            <Card 
              className="border-none"
              styles={{
                body: {
                  padding: '24px',
                },
              }}
              style={{
                borderRadius: '0px',
              }}
            >
            {/* Compact Patient Info */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <Title level={4} className="!mb-1 !text-gray-800">
                  {patient?.first_name} {patient?.last_name}
                </Title>
                <div className="flex items-center gap-4 text-sm">
                  {/* Phone */}
                  {patient?.mobile_no && (
                    <div className="flex items-center gap-2">
                      <Typography.Text className="text-gray-700 font-medium">
                        +{patient.mobile_no}
                      </Typography.Text>
                      <FaWhatsapp
                        className="text-green-500 text-sm cursor-pointer"
                        onClick={e => {
                          e.stopPropagation();
                          if (patient.mobile_no) {
                            window.open(
                              `https://web.whatsapp.com/send/?phone=${patient.mobile_no}`,
                              '_blank'
                            );
                          }
                        }}
                      />
                    </div>
                  )}
                  {/* Birth Date */}
                  {patient?.birth_date && (
                    <div className="flex items-center gap-1">
                      <FaBirthdayCake className="text-gray-500 text-xs" />
                      <Typography.Text className="text-gray-600 text-xs">
                        {dayjs(patient.birth_date).format('DD MMM, YYYY')}
                      </Typography.Text>
                    </div>
                  )}
                  {/* Gender */}
                  {patient?.gender && (
                    <div className="flex items-center gap-1">
                      {patient.gender === 'MALE' ? (
                        <ManOutlined className="text-blue-600 text-xs" />
                      ) : patient.gender === 'FEMALE' ? (
                        <WomanOutlined className="text-pink-600 text-xs" />
                      ) : (
                        <UserOutlined className="text-gray-500 text-xs" />
                      )}
                      <Typography.Text className="text-gray-600 text-xs">{patient.gender}</Typography.Text>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Patient Type Badges */}
              {patient?.type && (
                <div className="flex flex-wrap gap-1">
                  {getPatientTypeBadges(patient)}
                </div>
              )}
            </div>

            {/* Compact Action Buttons */}
            <div className="flex gap-2">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateAppointment}
                className="flex-1 h-8 rounded-lg text-xs"
                size="small"
              >
                Create Appointment
              </Button>
              <Button
                icon={<EyeOutlined />}
                onClick={handleViewAppointments}
                className="flex-1 h-8 rounded-lg border-blue-500 text-blue-500 hover:bg-blue-50 text-xs"
                size="small"
              >
                View Appointments
              </Button>
            </div>
          </Card>
        </div>

        {/* Tabbed Navigation */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          <Tabs
            activeKey={currentTab}
            onChange={handleTabChange}
            type="line"
            size="middle"
            tabPosition="top"
            className="px-4"
            items={items}
          />
        </div>

        {/* Tab Content - Only this content area should scroll */}
        <div className="flex-1 overflow-auto p-4">
          {items.find(item => item.key === currentTab)?.children}
        </div>
      </div>
    );
  }

  // Desktop version
  return (
    <div className="min-h-screen flex flex-col overflow-auto bg-white rounded-lg">
      {/* Patient Header */}
      <div className="flex items-start justify-between gap-4 px-6 pt-6">
        {/* Left Section - Patient Info */}
        <div className="flex-1">
          {/* Back Button and Patient Name */}
          <div className="flex items-center gap-3 mb-1">
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={handleBackClick}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
            ></Button>

            <span className="text-lg font-semibold text-gray-800">
              {patient?.id} {patient?.first_name} {patient?.last_name}
            </span>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={handleEditPatient}
              className="ml-2"
            >
              Edit Patient
            </Button>
            <Button
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleCreateAppointment}
              className="ml-2"
            >
              Create Appointment
            </Button>
            {patient?.first_name && (
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                onClick={handleViewAppointments}
                className="ml-2"
              >
                View Appointments
              </Button>
            )}
          </div>

          {/* Phone Number and Address in one row */}
          <div className="flex items-center gap-4">
            {/* Phone Number */}
            {patient?.mobile_no && (
              <div className="flex items-center gap-1">
                {patient.mobile_no && (
                  <Tooltip title="Open WhatsApp">
                    <FaWhatsapp
                      style={{ fontSize: '16px', color: '#25D366' }}
                      onClick={e => {
                        e.stopPropagation();
                        if (patient.mobile_no) {
                          window.open(
                            `https://web.whatsapp.com/send/?phone=${patient.mobile_no}`,
                            '_blank'
                          );
                        }
                      }}
                    />
                  </Tooltip>
                )}
                <span className="text-gray-600 text-md font-mono font-semibold">
                  +{patient.mobile_no}
                </span>
              </div>
            )}

            {/* Address */}
            {patient?.address && (
              <div className="flex items-center gap-1">
                <EnvironmentOutlined className="text-blue-600 text-md" />
                <span className="text-gray-600 text-md">{patient.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Metadata */}
        <div className="flex flex-col items-end gap-1 min-w-[200px]">
          {/* Date and Gender Row */}
          <div className="flex items-center gap-4">
            {patient?.birth_date && (
              <div className="flex items-center gap-1">
                <FaBirthdayCake className="text-gray-500 text-xs" />
                <span className="text-gray-600 text-xs">
                  {dayjs(patient.birth_date).format('DD MMM, YYYY')}
                </span>
              </div>
            )}

            {patient?.gender && (
              <div className="flex items-center gap-1">
                {patient.gender === 'MALE' ? (
                  <ManOutlined className="text-blue-600 text-xs" />
                ) : patient.gender === 'FEMALE' ? (
                  <WomanOutlined className="text-pink-600 text-xs" />
                ) : (
                  <UserOutlined className="text-gray-500 text-xs" />
                )}
                <span className="text-gray-600 text-xs">{patient.gender}</span>
              </div>
            )}

            {patient?.type && getTypeConfig(patient.type)}
          </div>

          {/* Second Row - Age */}
          <div className="flex items-center gap-3 self-end">
            {patient?.referred_by_name && (
              <div className="flex items-center gap-1">
                <FaUserMd className="text-gray-500 text-xs" />
                <span className="text-gray-600 text-xs">
                  {patient.referred_by_name} ({patient.referred_by_type})
                </span>
              </div>
            )}
            {patient?.follow_up_date && (
              <div className="flex items-center gap-1">
                <CalendarOutlined className="text-gray-500 text-xs" />
                <span className="text-gray-600 text-xs">
                  {patient?.follow_up_date
                    ? dayjs(patient.follow_up_date).format('DD MMM, YYYY HH:mm')
                    : '-'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <Tabs
        activeKey={currentTab}
        onChange={handleTabChange}
        type="line"
        size="middle"
        tabPosition="top"
        className="flex-1 flex flex-col"
        tabBarStyle={{
          paddingLeft: 20,
        }}
        items={items}
      />

      {/* Patient Edit Form */}
      <EditForm
        resource="patients"
        fields={formFields as any}
        title="Edit Patient"
        submitText="Save Changes"
        initialValues={patientData || {}}
        open={editFormVisible}
        onClose={() => setEditFormVisible(false)}
        onSuccess={handlePatientEdited}
        formDisplayMode="drawer"
        drawerWidth={1200}
      />

      {/* Appointment Creation Form */}
      <CreateForm
        resource="appointments"
        fields={appointmentFormFields as any}
        title="Create New Appointment"
        submitText="Create Appointment"
        open={createAppointmentFormVisible}
        onClose={() => setCreateAppointmentFormVisible(false)}
        onSuccess={handleAppointmentCreated}
        formDisplayMode="drawer"
        drawerWidth={1200}
        initialValues={{
          patient_name: `${patient?.first_name} ${patient?.last_name || ''}`.trim(),
          patient_mobile_no: patient?.mobile_no,
          patient_id: patient?.id,
        }}
      />
    </div>
  );
};
